<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BotService from '../services/bot';
import { useBotsStore } from '@/stores/bots';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { useLivechatStore } from '@/stores/livechat';
import SearchComponent from '@/components/SearchComponent.vue';
import { TrackingService } from '@/services/tracking';
import HubNotification from '@/components/hub/HubNotification.vue';
import HubBotSelect from '@/components/hub/HubBotSelect.vue';
import LivechatHeaderBtn from '@/components/livechat/LivechatHeaderBtn.vue';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { breakpoints } from '@/utils/breakpoints';
import { MessagesSquare, Truck } from '@lucide/vue';
import facebookIconUrl from '@/assets/brands/facebook.svg';

/** Top application bar: bot selector/title, search, and channel/deploy actions. */
const botsStore = useBotsStore();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();
const livechatStore = useLivechatStore();
const { t } = useI18n();

const isDesktop = breakpoints.greaterOrEqual('sm');

const currentBot = computed(() => botsStore.currentBotGetter);
const currentBotId = computed(() => botsStore.currentBotId);
const isMultiBot = computed(() => authStore.isMultiBot);
const isOnlyStaging = computed(() => authStore.isOnlyStaging);
const isSimpleEdit = computed(() => authStore.isSimpleEdit);
const notification = computed(() => notificationsStore.notification);
const botHasLivechat = computed(() => livechatStore.botHasLivechatGetter);

const facebookChannel = computed(() =>
  currentBot.value ? BotService.linkToChannel(currentBot.value, 'facebook') : false,
);
const hasFacebookChannel = computed(() =>
  currentBot.value ? BotService.hasActiveChannel(currentBot.value, 'facebook') : false,
);
const hasWebchatChannel = computed(() =>
  currentBot.value ? BotService.hasActiveChannel(currentBot.value, 'widget') : false,
);

function handleBtnClick(routerPath: string) {
  TrackingService.trackMenu({
    botId: currentBotId.value,
    type: 'click',
    source: 'Appbar',
    payload: { routerPath },
  });
}

onMounted(() => {
  botsStore.setViewStaging(!!isOnlyStaging.value);
});
</script>

<template>
  <div class="flex flex-col">
    <div class="flex items-center gap-2 bg-ci-space-cadet p-2 text-white">
      <slot name="left" />

      <HubBotSelect v-if="isMultiBot" class="mr-2" data-cy="app-bar-bot-select" />

      <template v-else>
        <!-- A label, not the page title (that's the page's own <h1>); font-geometric
             keeps the Silka font the global h1 rule used to give it. -->
        <span
          v-if="currentBot"
          class="mr-2 hidden font-geometric text-xl font-normal sm:flex lg:pl-2"
          data-cy="app-bar-bot-title"
        >
          {{ currentBot.name }}
        </span>
      </template>

      <div class="flex-1" />

      <SearchComponent />

      <LivechatHeaderBtn v-if="botHasLivechat" />

      <Separator orientation="vertical" class="mx-1 !h-6 bg-white/15" />

      <Button
        v-if="hasWebchatChannel"
        variant="ghost"
        class="text-white/80 hover:bg-white/10 hover:text-white"
        :class="{ 'w-9 px-0': !isDesktop }"
        target="_blank"
        data-cy="app-bar-preview-btn"
        :to="{ name: 'preview', params: { botId: currentBot.stagingBot } }"
        @click="handleBtnClick('preview')"
      >
        <MessagesSquare class="size-4" />
        <span class="hidden sm:inline">{{ $t('common.preview') }}</span>
      </Button>

      <Button
        v-if="isSimpleEdit"
        class="text-ci-space-cadet"
        variant="secondary"
        :to="{ name: 'deployment-details' }"
        data-cy="app-bar-deployment-btn"
        @click="handleBtnClick('deployment-details')"
      >
        <Truck class="size-4" />
        <span class="hidden md:inline">{{ t('settings.deployment.deploy') }}</span>
      </Button>
      <Button
        v-else
        disabled
        variant="secondary"
        data-cy="app-bar-deployment-btn"
      >
        <Truck class="size-4" />
        <span class="hidden md:inline">{{ t('settings.deployment.deploy') }}</span>
      </Button>

      <Button
        v-if="hasFacebookChannel"
        variant="ghost"
        size="icon"
        class="text-white hover:bg-white/10 hover:text-white"
        :href="facebookChannel || undefined"
        target="_blank"
      >
        <img :src="facebookIconUrl" alt="" class="h-5 w-5" />
      </Button>
    </div>

    <div v-if="notification" class="grow bg-white px-2 pt-2">
      <HubNotification />
    </div>
  </div>
</template>
